import type { ProfileField, ProfileFieldValue } from "../../types";
import {
  fieldAllowsOtherValue,
  hasOtherOption,
  isCustomOtherValue,
  isOtherOptionValue,
  normalizeProfileFieldValue,
} from "../../utils/profileFields";
import { validateCustomRule } from "./validation/validate";

export function isMultiValueDynamicField(field: ProfileField): boolean {
  return field.type === "multiSelect" || field.type === "checkbox";
}

export function initialDynamicFieldValue(field: ProfileField): ProfileFieldValue {
  const normalizedValue = normalizeProfileFieldValue(field, undefined);
  return Array.isArray(normalizedValue) ? [] : null;
}

export function isFieldMasked(
  field: ProfileField,
  formValues: Record<string, ProfileFieldValue> | undefined,
): boolean {
  if (!field.maskingRule?.length) {
    return false;
  }

  return !validateCustomRule(field.maskingRule, formValues ?? {});
}

export function validateDynamicField(
  field: ProfileField,
  value: ProfileFieldValue | undefined,
  formValues?: Record<string, ProfileFieldValue>,
): true | string {
  if (isFieldMasked(field, formValues)) {
    return true;
  }

  const normalizedValue = normalizeProfileFieldValue(field, value);
  const textValue =
    typeof normalizedValue === "string" ? normalizedValue.trim() : "";

  if (field.required) {
    const missing = Array.isArray(normalizedValue)
      ? normalizedValue.length === 0
      : !textValue;
    if (missing) {
      return `Please enter ${field.label.toLowerCase()}.`;
    }
  }

  if (fieldAllowsOtherValue(field)) {
    if (Array.isArray(normalizedValue)) {
      const hasOtherSelection = normalizedValue.some(
        (item) => isOtherOptionValue(item) || isCustomOtherValue(field, item),
      );
      const hasCustomOtherEntry = normalizedValue.some((item) =>
        isCustomOtherValue(field, item),
      );

      if (field.required && hasOtherSelection && !hasCustomOtherEntry) {
        return `Please enter ${field.label.toLowerCase()}.`;
      }

      return true;
    }

    if (
      hasOtherOption(field) &&
      textValue &&
      !isOtherOptionValue(textValue) &&
      isCustomOtherValue(field, textValue)
    ) {
      return true;
    }
  }

  if (!textValue) {
    return true;
  }

  switch (field.type) {
    case "email":
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(textValue)
        ? true
        : "Please enter a valid email address.";
    case "postcode":
      return /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i.test(textValue)
        ? true
        : "Please enter a valid postcode.";
    case "mobile":
      return isValidUkPhone(textValue, true)
        ? true
        : "Please enter a valid mobile number.";
    case "landline":
      return isValidUkPhone(textValue, false)
        ? true
        : "Please enter a valid landline number.";
    case "number":
      return Number.isNaN(Number(textValue))
        ? "Please enter a valid number."
        : true;
    case "date":
    case "datetime":
      return Number.isNaN(new Date(textValue).getTime())
        ? `Please enter a valid ${field.type === "date" ? "date" : "date and time"}.`
        : true;
    default:
      return true;
  }
}

function isValidUkPhone(value: string, mobileOnly: boolean): boolean {
  const normalized = value.replace(/[\s()-]/g, "");
  const international = normalized.replace(/^\+44/, "0");

  if (!/^0\d{9,10}$/.test(international)) {
    return false;
  }

  return mobileOnly
    ? /^07\d{9}$/.test(international)
    : !/^07\d{9}$/.test(international);
}
